/**
 * NebulaTide - De app die de oceaan redt!
 * @format
 * 
 * Even testen of de build nog snel genoeg is
 */

import React, { useEffect, useState } from 'react';
import { StatusBar, StyleSheet, View, Text, ActivityIndicator, TouchableOpacity } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import { initializeDatabase } from './database/init';
import { languageService } from './services';
import { RootNavigator } from './navigation';
import { FirstRunLanguageScreen } from './screens';
import type { AppError } from './models';
import { isApplicatieFout } from './models/AppError';
import { getErrorMessage, getErrorTitle, getErrorActionText } from './utils';
import './locales'; // Talen inladen (anders snapt niemand er iets van)

function App() {
  // Staat van de database (is ie al wakker?)
  const [isDbReady, setIsDbReady] = useState(false);
  // Moeten we vragen welke taal ze spreken?
  const [needsLanguageSetup, setNeedsLanguageSetup] = useState(false);
  // Ging er iets goed mis?
  const [dbError, setDbError] = useState<AppError | null>(null);
  const { t } = useTranslation();

  // Dit is de grote start functie.
  // Hier zorgen we dat alles klaarstaat voordat we de gebruiker loslaten.
  // Beetje zoals koffie zetten voor je begint met werken.
  const setupApp = async () => {
    try {
      setDbError(null);

      // Kijken of we al een taal hebben gekozen
      // Als ze dat nog niet hebben gedaan, moeten ze dat eerst doen
      const hasLanguage = await languageService.hasSavedLanguage();
      if (!hasLanguage) {
        setNeedsLanguageSetup(true);
      }

      // Opgeslagen taal laden (als die er is)
      await languageService.loadSavedLanguage();

      // Database aanzwengelen
      // Zonder database zijn we nergens
      await initializeDatabase();
      setIsDbReady(true);
    } catch (error) {
      console.error('Kon app niet starten:', error);

      if (isApplicatieFout(error)) {
        setDbError(error);
      } else {
        // Als we echt niet weten wat er mis is, maken we er zelf maar wat van.
        // Hopelijk komt de gebruiker dit nooit tegen 🤞 (klopt af).
        const appError = new Error(
          error instanceof Error ? error.message : 'Onbekende fout'
        ) as AppError;
        appError.type = 'database_init';
        appError.recoverable = true;
        appError.retryAction = setupApp;
        setDbError(appError);
      }
    }
  };

  useEffect(() => {
    setupApp();
  }, []);

  // Laden... 
  // Dit laten we zien terwijl we de database wakker maken
  if (!isDbReady && !dbError) {
    return (
      <SafeAreaProvider>
        <View style={styles.container}>
          <ActivityIndicator size="large" color="#7B3FF2" />
          <Text style={styles.info}>{t('common.loading')}</Text>
        </View>
      </SafeAreaProvider>
    );
  }

  // Oeps, er is iets stuk
  // Hier laten we een mooie foutmelding zien zodat ze weten wat ze moeten doen
  if (dbError) {
    const errorTitle = getErrorTitle(dbError, t);
    const errorMessage = getErrorMessage(dbError, t);
    const actionText = getErrorActionText(dbError, t);

    return (
      <SafeAreaProvider>
        <View style={styles.container}>
          <Text style={styles.errorIcon}>⚠️</Text>
          <Text style={styles.error}>{errorTitle}</Text>
          <Text style={styles.errorMessage}>{errorMessage}</Text>
          {dbError.recoverable && actionText && (
            <TouchableOpacity
              style={styles.retryButton}
              onPress={() => {
                if (dbError.retryAction) {
                  dbError.retryAction();
                } else {
                  setupApp();
                }
              }}
              accessibilityLabel={actionText}
              accessibilityRole="button">
              <Text style={styles.retryButtonText}>{actionText}</Text>
            </TouchableOpacity>
          )}
        </View>
      </SafeAreaProvider>
    );
  }

  return (
    <SafeAreaProvider>
      <StatusBar barStyle="dark-content" backgroundColor="#F5F5F5" />

      {needsLanguageSetup ? (
        <FirstRunLanguageScreen onLanguageSelected={() => setNeedsLanguageSetup(false)} />
      ) : (
        <RootNavigator />
      )}
    </SafeAreaProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
    padding: 20,
  },
  info: {
    fontSize: 14,
    color: '#666',
    marginTop: 16,
  },
  errorIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  error: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#EF4444',
    marginBottom: 8,
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    paddingHorizontal: 32,
    marginBottom: 24,
    lineHeight: 24,
  },
  retryButton: {
    backgroundColor: '#4A90E2',
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 24,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  retryButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
});

export default App;
